import type { BoxProps } from '@mui/material'
import { Box, styled, useTheme } from '@mui/material'

const ContentPaper = styled('div', {
  shouldForwardProp: prop => prop !== 'columnCount' && prop !== 'columnFill',
})<{ columnCount?: number; columnFill?: string }>(({ theme, columnCount = 1, columnFill = 'balanced' }) => ({
  display: 'block',
  /** @see https://www.smashingmagazine.com/2019/01/css-multiple-column-layout-multicol/ */
  ...(columnCount > 1
    ? {
        columnWidth: 'auto',
        columnCount,
        columnGap: '1em',
        columnRule: `2px solid ${theme.vars.palette.divider}`,
        columnFill: columnFill as never,
        height: '100%',
      }
    : {}),
  padding: theme.spacing(1, 1, 1, 1),

  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}))

export interface ContentLayoutProps extends Omit<BoxProps, 'component'> {
  className?: string
  size: 'small' | 'medium' | 'large'
  columns?: number
  columnFill?: 'auto' | 'balanced'
}

export const ContentLayout = (props: ContentLayoutProps) => {
  const { className, children, columns = 1, columnFill, size = 'large', sx, ref, ...restProps } = props
  const theme = useTheme()

  return (
    <Box
      {...restProps}
      sx={{
        fontSize: size === 'small' ? '0.8rem' : size === 'medium' ? '1rem' : '1.2rem',
        flex: 1,
        overflow: 'auto',
        margin: 0,
        [theme.breakpoints.down('sm')]: {
          margin: 0,
        },
        [theme.breakpoints.up('sm')]: {
          margin: 'auto',
          width: 'min(1024px, 100%)',
          padding: theme.spacing(2, 4, 2, 4),
        },
        ...sx,
      }}
      ref={ref}
      className={className}
    >
      <ContentPaper columnCount={columns} columnFill={columnFill}>
        {children}
      </ContentPaper>
    </Box>
  )
}

export default ContentLayout
